import RAGServiceV2 from '@/services/rag_v2';
import { useBotsStore } from '@/stores/bots';
import { useKnowledgeBaseStore } from '@/stores/knowledgeBase';

/** The backend type discriminator of a RAG resource (what kind of knowledge source it is) */
export type RAGResourceType = 'QNA' | 'MARKDOWN' | 'WEBPAGE' | 'PDF' | 'TABLE' | 'EXTERNAL';

/**
 * A resource ↔ intent connection entry (`resource.intents[n]`), tracking which
 * channels the connection is active in per environment.
 */
export interface RagIntent {
  /** The intent's technical name this resource is connected to */
  intent: string;
  /** Channel IDs where the connection is live. First channel / `null` is normalized to the string `"null"` */
  liveChannels?: string[];
  /** Channel IDs where the connection is enabled in staging. Same `"null"` normalization as live */
  stagingChannels?: string[];
}

/**
 * What channel logic to use. Pick only one.
 */
export interface AssignmentChannelOptions {
  /** Target all channels. */
  all?: boolean | null;
  /** Target a specific channel. It can also be `null`, "null", or the ID of the first channel. */
  channelId?: string | 'null' | null;
  /** Target the current channel you are in (bots/currentChannelId). Default behavior when nothing else is set. */
  current?: boolean | null;
}

/**
 * These enums let you know mostly the "state" of the document relative to its status in the database.
 */
export const ResourceDraftType = {
  /**
   * This indicates a new document that has never been published.
   * This happens when you create a new document and are taken to the editor.
   * This document is populated in the Vuex store when you create a new document.
   */
  NEW_DRAFT: 'newDraft',
  /**
   * The document is published and is an unmodified version you can compare edits against.
   * It is the document that is loaded and populated into the Content Vuex store.
   * Do not make changes to this document, use the DRAFT copy instead.
   */
  PUBLISHED: 'published',
  /**
   * This indicates a published document that has been edited.
   * It will exist alongside the PUBLISHED one.
   * This copy is automatically populated in the Vuex store when you start editing a document.
   */
  DRAFT: 'draftOfPublished',
}

/**
 * Where we can find the Markdown content for a given type of RAG resource
 */
export const MarkdownPath: Record<string, string> = {
  MARKDOWN: 'content.markdown',
  EXTERNAL: 'content.markdown',
  QNA: 'content.answer',
  WEBPAGE: 'markdown',
  PDF: 'markdown',
  TABLE: 'markdown',
  undefined: 'markdown',
};
/**
 * Where we can find the display title stored for a given type of RAG resource
 */
export const TitlePath: Record<string, string> = {
  MARKDOWN: 'metaData.title',
  QNA: 'content.question',
  QNAFallback: 'metaData.originalTitle',
  PDF: 'url',
  WEBPAGE: 'url',
  TABLE: 'url',
  EXTERNAL: 'metaData.title',
};
/**
 * Types of RAG resources that we can use the Markdown editor on.
 * Specifically, ones that has to be written in Markdown before they can get created.
 */
export const EditableTypes: RAGResourceType[] = [
  'QNA',
  'MARKDOWN',
];

/**
 * Resources that are considered "public" or "external" knowledge.
 * Usually, we can display links to these in the widget as sources for the generated answers.
 */
export const PublicTypes: RAGResourceType[] = [
  'WEBPAGE', 
  'PDF',
];

/**
 * Determine if the type of resource is internal knowledge (private) or external knowledge (shown to user)
 * @param resource A RAG resource (or at least its `type`, `url` and `privacy` fields)
 */
export const isExternalResource = (resource: { type: RAGResourceType, privacy?: string, url?: string, [key: string]: any }): boolean => {
  // Support for external resources that contains a URL
  if (resource.type === 'EXTERNAL' && resource.url && resource.privacy !== 'private') {
    return true;
  }

  return PublicTypes.includes(resource.type) && resource.privacy !== 'private';
}

/**
 * Turns an SVG source string into a data URI, so previews never hit the network.
 */
const svgDataUri = (svg: string): string => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg.trim())}`;

export const illustrations: Record<string, string> = {
  /**
   * A stand-in "product photo" for the image display mode preview.
   * It is portrait on purpose: that is the aspect ratio where filling a
   * landscape card image area (crop) and fitting it (letterbox) actually
   * look different. Inlined as a data URI so the preview never hits the network.
   */
  imageFillMode: svgDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 120">
  <rect width="90" height="120" fill="#ffffff"/>
  <rect x="1" y="1" width="88" height="118" fill="none" stroke="#dfe3e6" stroke-width="2"/>
  <ellipse cx="45" cy="104" rx="27" ry="5" fill="#eceff1"/>
  <rect x="41" y="14" width="8" height="16" rx="2" fill="#90a4ae"/>
  <path d="M33 30h24a10 10 0 0 1 10 10v52a8 8 0 0 1-8 8H31a8 8 0 0 1-8-8V40a10 10 0 0 1 10-10z" fill="#5c9ce6"/>
  <rect x="27" y="56" width="36" height="18" rx="2" fill="#ffffff" opacity="0.85"/>
</svg>
`),
  /**
   * Illustrations for the card source picker.
   *
   * 'llm' shows a single card whose text is being written for you (sparkle).
   * 'action' shows a response array on the left, where each entry maps to one
   * card on the right — the colour pairing is what carries the "one card per
   * item" idea that the wording alone struggles with.
   */
  llmFillMode: svgDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 84">
  <rect x="34" y="6" width="92" height="72" rx="6" fill="#ffffff" stroke="#dfe3e6" stroke-width="1.5"/>
  <path d="M35 12a5 5 0 0 1 5-5h80a5 5 0 0 1 5 5v22H35z" fill="#eaf1fb"/>
  <rect x="44" y="44" width="56" height="7" rx="3.5" fill="#c9d3dd"/>
  <rect x="44" y="57" width="72" height="5" rx="2.5" fill="#dde3e9"/>
  <rect x="44" y="67" width="44" height="5" rx="2.5" fill="#dde3e9"/>
  <path d="M18 22l3 7.4L28.4 32 21 34.6 18 42l-3-7.4L7.6 32 15 29.4z" fill="#5700d0"/>
  <path d="M141 50l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" fill="#5700d0" opacity="0.55"/>
</svg>
`),
  actionFillMode: svgDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 84">
  <rect x="4" y="12" width="52" height="60" rx="5" fill="#f5f7f9" stroke="#dfe3e6" stroke-width="1.5"/>
  <circle cx="14" cy="26" r="3" fill="#5700d0"/>
  <rect x="21" y="23.5" width="26" height="5" rx="2.5" fill="#c9d3dd"/>
  <circle cx="14" cy="42" r="3" fill="#2e9e6b"/>
  <rect x="21" y="39.5" width="26" height="5" rx="2.5" fill="#c9d3dd"/>
  <circle cx="14" cy="58" r="3" fill="#e0863a"/>
  <rect x="21" y="55.5" width="26" height="5" rx="2.5" fill="#c9d3dd"/>
  <path d="M62 42h10m0 0l-3.5-3.5M72 42l-3.5 3.5" fill="none" stroke="#90a4ae" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <g stroke="#dfe3e6" stroke-width="1.5">
    <rect x="80" y="18" width="24" height="48" rx="4" fill="#ffffff"/>
    <rect x="107" y="18" width="24" height="48" rx="4" fill="#ffffff"/>
    <rect x="134" y="18" width="24" height="48" rx="4" fill="#ffffff"/>
  </g>
  <path d="M81 23a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v11H81z" fill="#ece3fb"/>
  <path d="M108 23a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v11h-22z" fill="#e2f3ea"/>
  <path d="M135 23a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v11h-22z" fill="#fbeee1"/>
  <g fill="#c9d3dd">
    <rect x="85" y="41" width="14" height="4" rx="2"/>
    <rect x="112" y="41" width="14" height="4" rx="2"/>
    <rect x="139" y="41" width="14" height="4" rx="2"/>
  </g>
  <g fill="#dde3e9">
    <rect x="85" y="50" width="10" height="3.5" rx="1.75"/>
    <rect x="112" y="50" width="10" height="3.5" rx="1.75"/>
    <rect x="139" y="50" width="10" height="3.5" rx="1.75"/>
  </g>
</svg>
`),
}


/**
 * Small utility to make it easy to manage resource connections to intents, across channels.
 * Returns a new array of connections you replace the old one with.
 * @example
 * async function onDisconnectFromFallback(resourceId) {
 *   const resource = this.resources.find(r => r._id === resourceId);
 *   const fallbackIntentId = this.fallbackFaqIntent.intent;
 *
 *   const connections = ResourceConnectionUtil.disconnectFromStaging(
 *    resource.intents,
 *    fallbackIntentId,
 *    { channelId: this.currentChannelId },
 *   );
 *   resource.intents = connections;
 *
 *   await RAGService.updateResource(resource);
 * }
 */
export const ResourceConnectionUtil = {
  /**
   * Resolve channel IDs to work with
   * @param channelOpts Channel targeting options (see {@link AssignmentChannelOptions})
   * @returns One or more channel IDs to target
   * @private
   */
  _channelId(channelOpts: AssignmentChannelOptions = {}): string[] {
    /**
     * First channelId or `null` always get changed to the string "null".
     */
    const botsStore = useBotsStore();

    // Apply to all channels
    if (channelOpts.all) {
      const channelIds = botsStore.currentChannels.map((ch: any) => ch.channelId);
      channelIds[0] = "null";
      return channelIds;
    }

    // Apply to current channel
    if (channelOpts.channelId === undefined) {
      const chId = botsStore.currentChannelIdGetter;
      if (botsStore.currentChannels[0].channelId === chId) return ["null"];
      return [chId];
    }

    // Apply to specified channel
    if ([null, "null", botsStore.currentChannels?.[0]?.channelId].includes(channelOpts.channelId)) return ["null"];
    return [channelOpts.channelId as string];
  },

  /**
   Whether an intent is active in a given channel's live environment
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   * @param intent The intent you want connected
   * @param channelOpts Specify the channel(s) to connect to (defaults to the current channel)
   */
  isInLive(intents: RagIntent[], intent: string, channelOpts: AssignmentChannelOptions = {}): boolean {
    for (const intentObj of intents) {
      if (intentObj.intent !== intent) continue;
      const channelIds = ResourceConnectionUtil._channelId(channelOpts);
      return intentObj?.liveChannels?.some(chId => channelIds.includes(chId)) ?? false;
    }

    return false;
  },
  /**
   * Checks whether a resource is enabled in live for an intent, in the given/current channel
   * @param resourceId The Object ID of the resource
   * @param intentObj The intent object (carries `resources.live`/`resources.staging` per channel)
   * @param channelOpts Specify the channel(s) to check (defaults to the current channel)
   */
  isInLiveV2(resourceId: string, intentObj: any, channelOpts: AssignmentChannelOptions = {}): boolean {
    if (!resourceId) return false;
    if (!intentObj) return false;

    const resources = Object.assign({live: {}, staging: {}}, intentObj.resources);
    const channelId = ResourceConnectionUtil._channelId(channelOpts)[0];
    return !!resources?.live?.[channelId+'']?.includes(resourceId);
  },

  /**
   * Whether an intent is active in a given channel's staging environment
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   * @param intent The intent you want connected
   * @param channelOpts Specify the channel(s) to connect to (defaults to the current channel)
   * @deprecated Resources no longer contain the `intents` object by default. Use `isInStagingV2`
   */
  isInStaging(intents: RagIntent[], intent: string, channelOpts: AssignmentChannelOptions = {}): boolean {
    for (const intentObj of intents) {
      if (intentObj.intent !== intent) continue;
      const channelIds = ResourceConnectionUtil._channelId(channelOpts);
      return intentObj?.stagingChannels?.some(chId => channelIds.includes(chId)) ?? false;
    }

    return false;
  },
  /**
   * Checks whether a resource is enabled in staging for an intent, in the given/current channel
   * @param resourceId The Object ID of the resource
   * @param intentObj The intent object (carries `resources.live`/`resources.staging` per channel)
   * @param channelOpts Specify the channel(s) to check (defaults to the current channel)
   */
  isInStagingV2(resourceId: string, intentObj: any, channelOpts: AssignmentChannelOptions = {}): boolean {
    if (!resourceId) return false;
    if (!intentObj) return false;

    const resources = Object.assign({live: {}, staging: {}}, intentObj.resources);
    const channelId = ResourceConnectionUtil._channelId(channelOpts)[0];
    return !!resources?.staging?.[channelId+'']?.includes(resourceId);
  },

  /**
   * Whether an intent is active in either live or staging environment of the given channel
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   * @param intent The intent you want connected
   * @param channelOpts Specify the channel(s) to check in (defaults to the current channel)
   * @deprecated Resources no longer contain the `intents` object by default. Use `isInLiveOrStagingV2`
   */
  isInLiveOrStaging(intents: RagIntent[], intent: string, channelOpts: AssignmentChannelOptions = {}): boolean {
    return ResourceConnectionUtil.isInStaging(intents, intent, channelOpts)
      || ResourceConnectionUtil.isInLive(intents, intent, channelOpts);
  },
  /**
   * Whether an intent is active in either live or staging environment of the given channel
   * @param resourceId The ID of the resource to check
   * @param intentObj The intent to check if it is present in
   * @param channelOpts Specify the channel(s) to check in (defaults to the current channel)
   */
  isInLiveOrStagingV2(resourceId: string, intentObj: any, channelOpts: AssignmentChannelOptions = {}): boolean {
    return ResourceConnectionUtil.isInStagingV2(resourceId, intentObj, channelOpts)
      || ResourceConnectionUtil.isInLiveV2(resourceId, intentObj, channelOpts);
  },

  /**
   * Connects a given intent to Live and Staging.
   * Gives you a new copy of the intent array.
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   * @param intent The intent you want connected
   * @param channelOpts Specify the channel(s) to connect to (defaults to the current channel)
   */
  connectToLiveAndStaging(intents: RagIntent[], intent: string, channelOpts: AssignmentChannelOptions = {}): RagIntent[] {
    return ResourceConnectionUtil.connectToLive(
      ResourceConnectionUtil.connectToStaging(intents, intent, channelOpts),
      intent,
      channelOpts,
    );
  },

  /**
   * Connects a given intent to Staging.
   * Gives you a new copy of the intent array.
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   * @param intent The intent you want connected
   * @param channelOpts Specify the channel(s) to connect to (defaults to the current channel)
   */
  connectToStaging(intents: RagIntent[], intent: string, channelOpts: AssignmentChannelOptions = {}): RagIntent[] {
    const ragIntents: RagIntent[] = JSON.parse(JSON.stringify(intents));
    const channelIds = ResourceConnectionUtil._channelId(channelOpts);
    let wasFound = false;

    for (const ragIntent of ragIntents) {
      if (ragIntent.intent !== intent) continue;
      wasFound = true;

      ragIntent.stagingChannels = Array.from(new Set(channelIds.concat(ragIntent.stagingChannels ?? [])));
      break;
    }

    // Create the intent in the array if necessary
    if (!wasFound) {
      ragIntents.push({
        intent,
        liveChannels: [],
        stagingChannels: channelIds,
      });
    }

    return ragIntents;
  },

  /**
   * Connects a given intent to Live.
   * Gives you a new copy of the intent array.
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   * @param intent The intent you want connected
   * @param channelOpts Specify the channel(s) to connect to (defaults to the current channel)
   */
  connectToLive(intents: RagIntent[], intent: string, channelOpts: AssignmentChannelOptions = {}): RagIntent[] {
    const ragIntents: RagIntent[] = JSON.parse(JSON.stringify(intents));
    const channelIds = ResourceConnectionUtil._channelId(channelOpts);
    let wasFound = false;

    for (const ragIntent of ragIntents) {
      if (ragIntent.intent !== intent) continue;
      wasFound = true;

      ragIntent.liveChannels = Array.from(new Set(channelIds.concat(ragIntent.liveChannels ?? [])));
      break;
    }

    // Create the intent in the array if necessary
    if (!wasFound) {
      ragIntents.push({
        intent,
        liveChannels: channelIds,
        stagingChannels: [],
      });
    }

    return ragIntents;
  },

  /**
   * Removes a given intent from Live and Staging.
   * Gives you a new copy of the intent array.
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   * @param intent The intent you want to disconnect
   * @param channelOpts Specify the channel(s) to disconnect from (defaults to the current channel)
   */
  disconnectFromLiveAndStaging(intents: RagIntent[], intent: string, channelOpts: AssignmentChannelOptions = {}): RagIntent[] {
    return ResourceConnectionUtil.disconnectLive(
      ResourceConnectionUtil.disconnectStaging(intents, intent, channelOpts),
      intent,
      channelOpts,
    );
  },

  /**
   * Removes a given intent from Staging.
   * Gives you a new copy of the intent array.
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   * @param intent The intent you want disconnect
   * @param channelOpts Specify the channel(s) to disconnect from (defaults to the current channel)
   */
  disconnectStaging(intents: RagIntent[], intent: string, channelOpts: AssignmentChannelOptions = {}): RagIntent[] {
    const ragIntents: RagIntent[] = JSON.parse(JSON.stringify(intents));

    for (const ragIntent of ragIntents) {
      if (ragIntent.intent !== intent) continue;

      const channelIds = ResourceConnectionUtil._channelId(channelOpts);
      ragIntent.stagingChannels = ragIntent.stagingChannels?.filter(chId => !channelIds.includes(chId)) ?? [];

      /*
      // Remove the entire intent if it has nothing left in both live and staging
      if (ragIntent.liveChannels.length === 0 && ragIntent.stagingChannels.length === 0) {
        ragIntents.splice(ragIntents.indexOf(ragIntent), 1);
      }
       */

      break;
    }

    return ragIntents;
  },

  /**
   * Removes a given intent from Live.
   * Gives you a new copy of the intent array.
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   * @param intent The intent you want disconnect
   * @param channelOpts Specify the channel(s) to disconnect from (defaults to the current channel)
   */
  disconnectLive(intents: RagIntent[], intent: string, channelOpts: AssignmentChannelOptions = {}): RagIntent[] {
    const ragIntents: RagIntent[] = JSON.parse(JSON.stringify(intents));

    for (const ragIntent of ragIntents) {
      if (ragIntent.intent !== intent) continue;

      const channelIds = ResourceConnectionUtil._channelId(channelOpts);
      ragIntent.liveChannels = ragIntent.liveChannels?.filter(chId => !channelIds.includes(chId)) ?? [];

      /*
      // Remove the entire intent if it has nothing left in both live and staging
      if (ragIntent.liveChannels.length === 0 && ragIntent.stagingChannels.length === 0) {
        ragIntents.splice(ragIntents.indexOf(ragIntent), 1);
      }
       */

      break;
    }

    return ragIntents;
  },
  /**
   * Disconnects every channel in every environment for all intents
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   */
  disconnectEverything(intents: RagIntent[]): RagIntent[] {
    return intents.map(i => ({intent: i.intent, stagingChannels: [], liveChannels: []}))
  },

  /**
   * Returns {@link RagIntent}s that is used in staging, where your channel options match
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   * @param channelOpts The channel(s) to count in (defaults to all channels)
   * @param andIntentNames Optional: Filter further; only if it is connected to one of the intents provided
   */
  getIntentsInStaging(intents: RagIntent[], channelOpts: AssignmentChannelOptions = {all: true}, andIntentNames: string[] | null = null): RagIntent[] {
    if (!intents) return [];

    const channels = this._channelId(channelOpts);
    return intents.filter(intent => {
      if (andIntentNames && !andIntentNames.includes(intent.intent)) return false;
      return channels.some(chId => intent.stagingChannels?.includes(chId));
    });
  },

  /**
   * Returns {@link RagIntent}s that is used in staging, where your channel options match
   * @param intents A list of intents this resource is connected to (`resource.intents`)
   * @param channelOpts The channel(s) to count in (defaults to all channels)
   * @param andIntentNames Optional: Filter further; only if it is connected to one of the intents provided
   */
  getIntentsInLive(intents: RagIntent[], channelOpts: AssignmentChannelOptions = {all: true}, andIntentNames: string[] | null = null): RagIntent[] {
    if (!intents) return [];

    const channels = this._channelId(channelOpts);
    return intents.filter(intent => {
      if (andIntentNames && !andIntentNames.includes(intent.intent)) return false;
      return intent.liveChannels?.some(ch => channels.includes(ch));
    });
  },
}

export default {
  methods: {
    /**
     * Connect resources to intents, for given channels
     * @param resourceIds Object IDs of the resources to connect
     * @param intentNames Technical intent names to connect them to
     * @param channelOpts Channel targeting (defaults to the current channel)
     * @param environments Which environments to connect in (defaults to staging only)
     * @deprecated This will no longer work because resources does not hold information about intents by default
     */
    async connectToIntent(resourceIds: string[], intentNames: string[], channelOpts: AssignmentChannelOptions = {}, environments: { staging?: boolean, live?: boolean } = {staging: true}): Promise<void> {
      // Skip if nothing changed
      if (!environments.staging && !environments.live) return;

      // Get a copy of the resources, making it easy to roll-back mutations of connected intents if it fails
      const botsStore = useBotsStore();
      const knowledgeBaseStore = useKnowledgeBaseStore();
      const botId = botsStore.currentBotId;
      const resources = knowledgeBaseStore.resourcesGetter(botId, resourceIds);

      const results: Array<Promise<RagIntent[] | null>> = [];
      for (const resource of resources) {

        // Base the new list of intents on the existing one
        let newIntentList = resource.intents;

        // Build the new intent list iteratively
        for (const intentName of intentNames) {
          if (environments.live && environments.staging) newIntentList = ResourceConnectionUtil.connectToLiveAndStaging(resource.intents, intentName, channelOpts);
          else if (environments.staging) newIntentList = ResourceConnectionUtil.connectToStaging(resource.intents, intentName, channelOpts);
          else newIntentList = ResourceConnectionUtil.connectToLive(resource.intents, intentName, channelOpts);
        }

        // Update backend
        results.push(
          RAGServiceV2.setAssignedIntents(
            botId,
            resource._id,
            newIntentList
          ).then((result: any) => {
            if (result === null) {
              console.error("Failed to update intent list for resource", resource._id);
              return null;
            }

            return newIntentList;
          })
        );
      }

      // Overwrite instance for successful ones ...
      let i=-1;
      for await (const result of results) {
        i++;
        if (result === null) continue;
        resources[i].intents = result;
      }

      // ... and commit all to storage/make it reactive other places
      knowledgeBaseStore.setResources({ botId, resources });
    },
    /**
     * Disconnect resources from intents, in given channels
     * @param resourceIds Object IDs of the resources to disconnect
     * @param intentNames Technical intent names to disconnect them from
     * @param channelOpts Channel targeting (defaults to the current channel)
     * @param environments Which environments to disconnect in (defaults to staging only)
     */
    async disconnectFromIntent(resourceIds: string[], intentNames: string[], channelOpts: AssignmentChannelOptions = {}, environments: { staging?: boolean, live?: boolean } = {staging: true}): Promise<void> {
      // Skip if nothing changed
      if (!environments.staging && !environments.live) return;

      // Get a copy of the resources, making it easy to roll-back mutations of connected intents if it fails
      const botsStore = useBotsStore();
      const knowledgeBaseStore = useKnowledgeBaseStore();
      const botId = botsStore.currentBotId;
      const resources = knowledgeBaseStore.resourcesGetter(botId, resourceIds);

      const results: Array<Promise<RagIntent[] | null>> = [];
      for (const resource of resources) {
        // Base the new list of intents on the existing one
        let newIntentList = resource.intents;

        // Build the new intent list iteratively
        for (const intentName of intentNames) {
          if (environments.live && environments.staging) newIntentList = ResourceConnectionUtil.disconnectFromLiveAndStaging(resource.intents, intentName, channelOpts);
          else if (environments.staging) newIntentList = ResourceConnectionUtil.disconnectStaging(resource.intents, intentName, channelOpts);
          else newIntentList = ResourceConnectionUtil.disconnectLive(resource.intents, intentName, channelOpts);
        }

        // Update backend
        results.push(
          RAGServiceV2.setAssignedIntents(
            botId,
            resource._id,
            newIntentList
          ).then((result: any) => {
            if (result === null) {
              console.error("Failed to update intent list for resource", resource._id);
              return null;
            }

            return newIntentList;
          })
        );
      }

      // Overwrite instance for successful ones ...
      let i=-1;
      for await (const result of results) {
        i++;
        if (result === null) continue;
        resources[i].intents = result;
      }

      // ... and commit all to storage/make it reactive other places
      knowledgeBaseStore.setResources({ botId, resources });
    },
    /**
     * Disconnects all intents in every channel, for every environment.
     * A process that must be done before deleting a resource.
     * @param resourceIds Object IDs of the resources to fully disconnect
     */
    async disconnectAllIntents(resourceIds: string[]): Promise<void> {
      const botsStore = useBotsStore();
      const knowledgeBaseStore = useKnowledgeBaseStore();
      const botId = botsStore.currentBotId;
      const results: Array<Promise<string | null>> = [];

      // Disconnect in backend
      for (const resourceId of resourceIds) {
        results.push(
          RAGServiceV2.setAssignedIntents(
            botId,
            resourceId,
            ResourceConnectionUtil.disconnectEverything(
              knowledgeBaseStore.getResource(botId, resourceId).intents
            )
          ).then((result: any) => {
            if (result === null) {
              console.error("Failed to update intent list for resource", resourceId);
              return null;
            }
            return resourceId;
          })
        );
      }

      // Update the resource and storage
      for await (const idOrNull of results) {
        if (idOrNull === null) continue;
        const resource = knowledgeBaseStore.getResource(botId, idOrNull);
        if (!resource) continue;
        resource.intents = [];
        knowledgeBaseStore.setResources({botId, resources: [resource]});
      }
    }
  }
}
